<template>
    <div class="killerBg relative flex">
        <div
            class="killerBg splashAnimation absolute z-10 flex h-full w-full items-center justify-center"
        >
            <img
                src="@/assets/images/games/killer/killerSplash.svg"
                :alt="$t('killer.logo_alt')"
                class="absolute h-full max-w-[unset]"
            />
            <img
                src="@/assets/images/games/killer/killerTitle.svg"
                :alt="$t('killer.dartboard_alt')"
                class="max-w-120 pb-16"
            />
        </div>

        <transition name="fade-turn" mode="out-in">
            <KillerTurnSplash
                v-if="killerSwitched"
                :key="'killer-turn'"
                :profile="currentPlayer"
                :color="getProfileColor(currentPlayer?.id)"
            />
            <TurnSplash
                v-else-if="showTurnIndicator"
                :key="'regular-turn'"
                :profile="currentPlayer"
                :color="getProfileColor(currentPlayer?.id)"
            />
        </transition>
        <!-- <KillerTurnSplash :profileColor="currentPlayer" v-if="killerSwitched"/> -->
        <div
            class="flex h-full w-full flex-col items-center justify-between bg-[url('@/assets/images/games/killer/boardPattern.svg')] bg-size-[auto_100%] bg-center p-6"
        >
            <Board :sections="sections" />

            <div class="flex flex-col justify-center items-center" v-if="currentPlayer">
                <h2 class="font-title font-bold uppercase leading-3">{{ $t('play.your_turn') }}</h2>
                <h2 class="font-title font-bold uppercase" :style="{color: getProfileColor(currentPlayer?.id)}">{{ currentPlayer?.tag_name }}</h2>
                <p v-if="currentPlayerState?.is_killer" class="uppercase max-w-[60%] text-center text-sm mt-2 text-titles" style="line-height: 1.2">{{ $t('killer.killer_instructions') }}</p>
                <p v-else class="uppercase max-w-[60%] text-center text-sm mt-2 text-titles" style="line-height: 1.2">{{ $t('killer.become_killer_instructions') }}</p>
            </div>

            <KillerPlayerList
                v-if="currentPlayer"
                :current-player="currentPlayer"
                :profiles="profiles"
                :all-profile-states="allProfileStates"
                :get-profile-color="getProfileColor"
                @confirm-selection="onTargetConfirmed"
                @reset-selection="onTargetReset"
            />
        </div>
    </div>
</template>

<script>
import Board from '@/components/games/killer/Board.vue'
import KillerPlayerList from '@/components/games/killer/KillerPlayerList.vue'
import TurnSplash from '@/components/games/killer/TurnSplash.vue'
import { useMatchStore } from '@/stores/match.js'
import KillerTurnSplash from '@/components/games/killer/KillerTurnSplash.vue'

export default {
    name: 'KillerGame',
    components: {
        TurnSplash,
        Board,
        KillerPlayerList,
        KillerTurnSplash,
    },
    data() {
        return {
            playerColors: ['#0EC1FF', '#FF0000', '#C000FF', '#1AD200'],
            killerSwitched: false,
            showTurnIndicator: false,
        }
    },
    mounted() {
        console.log('KillerGame component mounted', this.matchStore.getProfiles)
    },
    computed: {
        matchStore() {
            return useMatchStore()
        },
        profiles() {
            return this.matchStore.getProfiles
        },
        currentPlayer() {
            return this.matchStore.getCurrentPlayer
        },
        sections() {
            return this.generateSections()
        },
        allProfileStates() {
            const matchData = this.matchStore.getMatchData
            if (!matchData?.sets?.length) return []

            const currentSet = matchData.sets[matchData.sets.length - 1]
            if (!currentSet?.legs?.length) return []

            const currentLeg = currentSet.legs[currentSet.legs.length - 1]
            if (!currentLeg?.turns?.length) return []

            const lastTurn = currentLeg.turns[currentLeg.turns.length - 1]
            return lastTurn.team_states || []
        },
        currentPlayerState() {
            if (!this.currentPlayer) return null
            return this.allProfileStates.find((state) => state.team === this.currentPlayer.team)
        },
        isCurrentPlayerKiller() {
            return this.currentPlayerState?.is_killer || false
        },
    },
    methods: {
        quitMatch() {
            console.log('Quitting match, clearing match data')
            this.matchStore.clearMatch()
            this.$router.push({ name: 'games' })
        },
        generateSections() {
            const sections = {}
            const dartboardOrder = [
                3, 17, 2, 15, 10, 6, 13, 4, 18, 1, 20, 5, 12, 9, 14, 11, 8, 16, 7, 19,
            ]

            dartboardOrder.forEach((sectionNumber, dartboardIndex) => {
                const claimedBy = this.allProfileStates.find(
                    (state) => state.number === sectionNumber,
                )

                if (claimedBy && (claimedBy.lives || 0) > 0) {
                    let sectionColor = '#FFFFFF'
                    let sectionAvatar = null

                    const owner = this.profiles.find((p) => p.team === claimedBy.team)
                    if (owner) {
                        sectionColor = this.getProfileColor(owner.id)
                        sectionAvatar = owner.avatar
                    }

                    sections[sectionNumber] = {
                        angle: dartboardIndex * 18,
                        hits: claimedBy?.hits_on_own_target || 0,
                        color: sectionColor,
                        avatar: sectionAvatar,
                        isKiller: claimedBy?.is_killer || false,
                        lives: claimedBy?.lives || 0,
                    }
                }
            })

            return sections
        },
        onTargetConfirmed() {
            console.log('✅ Target/darts confirmed and submitted')
        },
        onTargetReset() {
            console.log('🔄 Target selection reset')
        },
        getProfileColor(profileId) {
            const profileIndex = this.profiles.findIndex((p) => p.id === profileId)
            return this.playerColors[profileIndex % this.playerColors.length] || '#FFFFFF'
        },
    },
    watch: {
        isCurrentPlayerKiller() {
            if (this.isCurrentPlayerKiller) {
                this.killerSwitched = true
                setTimeout(() => {
                    this.killerSwitched = false
                }, 1500)
            }
        },
        currentPlayer(from, to) {
            if (this.currentPlayerState && from.id !== to.id) {
                this.showTurnIndicator = true
                setTimeout(() => {
                    this.showTurnIndicator = false
                }, 1500)
            }
        },
    },
}
</script>

<style lang="scss">
.killerBg {
    height: 100dvh;
    width: 100vw;
    background: url('@/assets/images/games/killer/killerBg.png');
    background-size: auto 100%;
}

.splashAnimation {
    // display: none;
    animation: splashOut 2s 1 forwards;
    @keyframes splashOut {
        0% {
            display: flex;
            opacity: 1;
        }
        95% {
            display: flex;
            opacity: 1;
        }
        99% {
            display: flex;
            opacity: 0;
        }
        100% {
            display: none;
            opacity: 0;
        }
    }
}

.fade-turn-enter-active,
.fade-turn-leave-active {
    transition: opacity 0.3s;
}

.fade-turn-enter-from,
.fade-turn-leave-to {
    opacity: 0;
}
</style>
